import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { ChallengeStatus } from '@/api/generated';
import { useLanguage } from '@/contexts/language-context';
import type { OtpFormData } from '@/form-schemas/otp-schemas';
import { otpFormDefaults, otpSchema } from '@/form-schemas/otp-schemas';
import type { UseMfaOrchestratorReturn } from '@/hooks/use-mfa-orchestrator.types';

interface UseOtpFormProps {
  submitOtp: UseMfaOrchestratorReturn['submitResponse'];
  maxRetries?: number;
}

export function useOtpForm({ submitOtp, maxRetries = 3 }: UseOtpFormProps) {
  const { t } = useLanguage();
  const [retryCount, setRetryCount] = useState(0);

  const form = useForm<OtpFormData>({
    resolver: zodResolver(otpSchema),
    defaultValues: otpFormDefaults,
    mode: 'onSubmit',
    reValidateMode: 'onChange',
  });

  const { formState, setError, clearErrors } = form;
  const { isSubmitting, errors } = formState;

  const onSubmit = async (data: OtpFormData) => {
    try {
      clearErrors();

      // Join the code array into a single string
      const otpCode = data.code.join('');

      const response = await submitOtp({
        otp_code: otpCode,
      });

      const errorStatuses = [
        ChallengeStatus.FAILED,
        ChallengeStatus.REJECTED,
        ChallengeStatus.RETRY_REQUIRED,
      ];

      if (errorStatuses.includes(response as ChallengeStatus)) {
        // Increment retry count
        const newRetryCount = retryCount + 1;
        setRetryCount(newRetryCount);

        // Keep the code visible for all error cases
        // This allows user to see what they entered and modify it

        // Check if max retries exceeded
        if (newRetryCount >= maxRetries) {
          setError('root', {
            type: 'manual',
            message: t('mfa.tooManyAttempts'),
          });
        } else {
          setError('root', {
            type: 'manual',
            message: t('mfa.invalidCode'),
          });
        }
      }
      // If response is FlowStatus.COMPLETED or APPROVED, let it proceed naturally
      // Don't reset form on success - let the page navigate away
    } catch (error) {
      console.error('OTP submission error:', error);
      // Increment retry count for exceptions too
      const newRetryCount = retryCount + 1;
      setRetryCount(newRetryCount);

      setError('root', {
        type: 'manual',
        message: t('mfa.invalidCode'),
      });
    }
  };

  const getErrorMessage = () => {
    if (errors.code) {
      return errors.code.message;
    }

    if (errors.root) {
      return errors.root.message;
    }

    return '';
  };

  return {
    form,
    onSubmit,
    isSubmitting,
    errors,
    getErrorMessage,
    hasExceededRetries: retryCount >= maxRetries,
  };
}
